import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, GoogleLogo, CreditCard, WhatsappLogo } from '@phosphor-icons/react';
import { openRazorpayCheckout } from '../utils/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseSubmission() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [subspecialties, setSubspecialties] = useState({});
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [caseId, setCaseId] = useState(null);
  const [formData, setFormData] = useState({
    symptoms: '',
    duration: '',
    current_diagnosis: '',
    medications: '',
    allergies: '',
    medical_history: '',
    specialty: '',
    subspecialty: '',
    tier: 'basic',
  });

  useEffect(() => {
    fetchSpecialties();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const { data } = await axios.get(`${API}/specialties`);
      setSpecialties(data.specialties || []);
      setSubspecialties(data.subspecialties || {});
    } catch (error) {
      console.error('Failed to fetch specialties');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty') {
      // Reset subspecialty when primary specialty changes
      setFormData({ ...formData, specialty: value, subspecialty: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!formData.specialty) {
      toast.error('Please select a specialty');
      return;
    }
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/cases`, formData, { withCredentials: true });
      setCaseId(data.case_id);
      toast.success('Case submitted successfully');
      setStep(2);
    } catch (error) {
      toast.error('Failed to submit case');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!caseId || files.length === 0) {
      setStep(3);
      return;
    }

    setLoading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/cases/${caseId}/files`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Files uploaded successfully');
      setStep(3);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };

  const [showManualUpi, setShowManualUpi] = useState(false);
  const [manualTxnId, setManualTxnId] = useState('');

  const handlePayment = async () => {
    setLoading(true);
    openRazorpayCheckout({
      packageId: formData.tier,
      referenceId: caseId,
      purpose: 'case_opinion',
      onSuccess: () => {
        toast.success('Payment successful! Your case is now active.');
        navigate('/patient');
      },
      onFailure: () => setLoading(false),
    });
  };

  const submitManualUpi = async () => {
    if (!manualTxnId.trim() || manualTxnId.trim().length < 6) {
      toast.error('Please enter a valid UPI transaction/reference ID');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/payments/manual-upi/confirm`,
        {
          reference_id: caseId,
          purpose: 'case_opinion',
          package_id: formData.tier,
          amount: formData.tier === 'basic' ? 1500 : 2500,
          transaction_id: manualTxnId.trim(),
        },
        { withCredentials: true }
      );
      toast.success('Payment confirmation submitted. Our team will verify within a few hours.');
      navigate('/patient');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit payment confirmation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="case-submission-page">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="card">
            <h1 className="font-manrope text-3xl font-bold text-primary mb-2">Submit Your Case</h1>
            <p className="text-secondary mb-8">Get expert medical opinion in 3 simple steps</p>

            {/* Progress */}
            <div className="flex items-center gap-4 mb-8">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                step >= 1 ? 'bg-primary text-white' : 'bg-surface-secondary text-secondary'
              }`}>
                1
              </div>
              <div className={`flex-1 h-1 ${
                step >= 2 ? 'bg-primary' : 'bg-surface-secondary'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                step >= 2 ? 'bg-primary text-white' : 'bg-surface-secondary text-secondary'
              }`}>
                2
              </div>
              <div className={`flex-1 h-1 ${
                step >= 3 ? 'bg-primary' : 'bg-surface-secondary'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                step >= 3 ? 'bg-primary text-white' : 'bg-surface-secondary text-secondary'
              }`}>
                3
              </div>
            </div>

            {/* Step 1: Case Details */}
            {step === 1 && (
              <form onSubmit={handleStep1Submit} className="space-y-6" data-testid="case-form-step1">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Specialty</label>
                  <select
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="specialty-select"
                  >
                    <option value="">Select Specialty</option>
                    {specialties.map((spec) => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>

                {formData.specialty && (subspecialties[formData.specialty] || []).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Subspecialty <span className="text-xs text-secondary">(optional — helps us route to the right expert)</span>
                    </label>
                    <select
                      name="subspecialty"
                      value={formData.subspecialty}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="subspecialty-select"
                    >
                      <option value="">Any subspecialty / Not sure</option>
                      {(subspecialties[formData.specialty] || []).map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Current Diagnosis</label>
                  <textarea
                    name="current_diagnosis"
                    value={formData.current_diagnosis}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="diagnosis-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Symptoms</label>
                  <textarea
                    name="symptoms"
                    value={formData.symptoms}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="symptoms-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Duration</label>
                  <input
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="e.g., 3 weeks"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="duration-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Current Medications (Optional)</label>
                  <textarea
                    name="medications"
                    value={formData.medications}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="medications-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Allergies (Optional)</label>
                  <input
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="allergies-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Medical History (Optional)</label>
                  <textarea
                    name="medical_history"
                    value={formData.medical_history}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="medical-history-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Select Tier</label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                      formData.tier === 'basic' ? 'border-primary bg-primary/5' : 'border-primary/20'
                    }`}>
                      <input
                        type="radio"
                        name="tier"
                        value="basic"
                        checked={formData.tier === 'basic'}
                        onChange={handleChange}
                        className="sr-only"
                        data-testid="tier-basic"
                      />
                      <div className="font-semibold text-primary mb-1">Basic — ₹1,500</div>
                      <div className="text-sm text-secondary">Written specialist opinion</div>
                    </label>
                    <label className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                      formData.tier === 'premium' ? 'border-primary bg-primary/5' : 'border-primary/20'
                    }`}>
                      <input
                        type="radio"
                        name="tier"
                        value="premium"
                        checked={formData.tier === 'premium'}
                        onChange={handleChange}
                        className="sr-only"
                        data-testid="tier-premium"
                      />
                      <div className="font-semibold text-primary mb-1">Premium — ₹2,500</div>
                      <div className="text-sm text-secondary">Detailed review + video consultation</div>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3 disabled:opacity-50"
                  data-testid="step1-submit-button"
                >
                  {loading ? 'Submitting...' : 'Continue to Upload Files'}
                </button>
              </form>
            )}

            {/* Step 2: File Upload */}
            {step === 2 && (
              <div className="space-y-6" data-testid="case-form-step2">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Upload Medical Records</label>
                  <p className="text-xs text-secondary mb-4">Upload PDF reports, images of scans, lab results, etc.</p>
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-primary/30 rounded-lg p-8 cursor-pointer hover:border-primary transition-all"
                    data-testid="file-upload-area"
                  >
                    <Upload size={48} className="text-primary mb-4" weight="duotone" />
                    <span className="text-primary font-medium">Click to upload files</span>
                    <span className="text-xs text-secondary mt-2">PDF, JPG, PNG, DICOM</span>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.dcm"
                    />
                  </label>
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-surface-secondary p-3 rounded-lg">
                        <span className="text-sm text-secondary truncate">{file.name}</span>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 border-2 border-primary text-primary font-semibold rounded-full hover:bg-primary hover:text-white transition-all"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleFileUpload}
                    disabled={loading}
                    className="flex-1 btn-primary py-3 disabled:opacity-50"
                    data-testid="step2-submit-button"
                  >
                    {loading ? 'Uploading...' : files.length > 0 ? 'Upload Files' : 'Skip'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Payment/Confirmation */}
            {step === 3 && (
              <div className="space-y-6" data-testid="case-form-step3">
                <div className="bg-surface-secondary rounded-lg p-6 text-center">
                  <h3 className="font-manrope text-xl font-semibold text-primary mb-2">Case Submitted Successfully!</h3>
                  <p className="text-secondary">
                    {formData.tier === 'basic'
                      ? 'Pay ₹1,500 to start your basic consultation. A specialist registered with the NMC or a State Medical Council will review your case within 48 hours.'
                      : 'Pay ₹2,500 to start your premium consultation with multi-specialist review and 30-60 min video call.'}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3 text-center">Choose Payment Method</p>

                  {/* Razorpay - Primary Indian Payment */}
                  <button
                    onClick={handlePayment}
                    disabled={loading}
                    className="w-full flex items-center justify-between gap-3 p-4 mb-3 rounded-lg border-2 border-primary bg-primary/5 hover:bg-primary/10 disabled:opacity-50 transition-all"
                    data-testid="payment-button"
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard size={28} weight="duotone" className="text-primary" />
                      <div className="text-left">
                        <div className="font-semibold text-primary">Pay with Razorpay (Recommended)</div>
                        <div className="text-xs text-secondary">Cards, UPI, Netbanking, Wallets · Auto-confirmation</div>
                      </div>
                    </div>
                    <span className="font-bold text-primary">
                      {loading ? '...' : (formData.tier === 'basic' ? '₹1,500' : '₹2,500')}
                    </span>
                  </button>

                  {/* GPay / UPI - direct VPA fallback */}
                  <a
                    href={`upi://pay?pa=shivaam.kesarwaani-1@oksbi&pn=SecondDoctorOpinion&am=${formData.tier === 'basic' ? '1500' : '2500'}&cu=INR&tn=${encodeURIComponent('Consultation-' + (caseId || ''))}`}
                    className="w-full flex items-center justify-between gap-3 p-4 mb-3 rounded-lg border-2 border-primary/20 hover:border-primary hover:bg-primary/5 transition-all"
                    data-testid="gpay-button"
                  >
                    <div className="flex items-center gap-3">
                      <GoogleLogo size={28} weight="duotone" className="text-primary" />
                      <div className="text-left">
                        <div className="font-semibold text-primary">Pay Directly via Google Pay / UPI</div>
                        <div className="text-xs text-secondary">UPI: shivaam.kesarwaani-1@oksbi · Manual confirmation via WhatsApp</div>
                      </div>
                    </div>
                    <span className="font-bold text-primary">
                      {formData.tier === 'basic' ? '₹1,500' : '₹2,500'}
                    </span>
                  </a>

                  {/* WhatsApp Help */}
                  <a
                    href={`https://wa.me/918840804798?text=${encodeURIComponent('Hi, I just submitted a consultation case and need help with payment. Case ID: ' + (caseId || ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-lg text-sm font-medium transition-all"
                    style={{ backgroundColor: '#25D366', color: 'white' }}
                    data-testid="whatsapp-help-button"
                  >
                    <WhatsappLogo size={20} weight="fill" />
                    Need help? Chat with us on WhatsApp
                  </a>

                  <p className="text-xs text-secondary mt-4 text-center">
                    After paying via GPay/UPI, either <strong>submit your UPI Transaction ID below</strong> or send a screenshot to WhatsApp <strong>+91 88408 04798</strong>.
                  </p>

                  {/* Manual UPI Confirmation toggle */}
                  <button
                    type="button"
                    onClick={() => setShowManualUpi(!showManualUpi)}
                    className="w-full mt-4 text-sm text-primary underline hover:no-underline"
                    data-testid="toggle-manual-upi"
                  >
                    {showManualUpi ? 'Hide UPI Transaction ID form' : 'I have already paid via UPI — Submit Transaction ID'}
                  </button>

                  {showManualUpi && (
                    <div className="mt-4 p-4 bg-surface-secondary rounded-lg border border-primary/20 space-y-3" data-testid="manual-upi-form">
                      <label className="block text-sm font-medium text-primary">UPI Transaction / Reference ID</label>
                      <input
                        type="text"
                        value={manualTxnId}
                        onChange={(e) => setManualTxnId(e.target.value)}
                        placeholder="e.g., 401123456789"
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="manual-txn-id-input"
                      />
                      <p className="text-xs text-secondary">You can find this in your GPay/PhonePe/Paytm app under transaction details.</p>
                      <button
                        type="button"
                        onClick={submitManualUpi}
                        disabled={loading}
                        className="w-full btn-primary py-2 disabled:opacity-50"
                        data-testid="submit-manual-upi-button"
                      >
                        {loading ? 'Submitting...' : 'Submit for Verification'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}